import { useState, useEffect } from 'react';
import { useAwningPricing } from './useAwningPricing';

export interface AwningModelCard {
  id: string;
  name: string;
  price: number;
  installCost: number;
  description: string;
  tagline: string;
  badge?: string;
  bullets: string[];
  bestFor: string;
  includes: string[];
  warrantyYears: number;
  awningType: string;
  smartHomeCompatible: boolean;
  windSensor: boolean;
}

export interface AwningAddOnCard {
  id: string;
  name: string;
  price: number;
  hasQuantity?: boolean;
  compatibleModels?: string[];
  description: string;
}

export const useAwningModels = () => {
  const { pricingByModel, models, addons, isLoading, error } = useAwningPricing();
  const [modelCards, setModelCards] = useState<AwningModelCard[]>([]);
  const [addonCards, setAddonCards] = useState<AwningAddOnCard[]>([]);

  useEffect(() => {
    if (!isLoading && models.length > 0) {
      // Helper function to get the lowest retail price and corresponding install cost for a model
      const getLowestPriceAndInstall = (modelId: string) => {
        const modelPricing = pricingByModel[modelId] || [];
        if (modelPricing.length === 0) {
          // Fallback to base price from model if no pricing data
          const model = models.find(m => m.model_id === modelId);
          return { price: model?.base_price || 0, installCost: 0 };
        }

        const prices = modelPricing
          .filter(p => p.retail_price > 0)
          .map(p => ({ retail: p.retail_price, install: p.install_cost }));

        if (prices.length === 0) {
          const model = models.find(m => m.model_id === modelId);
          return { price: model?.base_price || 0, installCost: 0 };
        }

        const lowestPriceItem = prices.reduce((min, current) =>
          current.retail < min.retail ? current : min
        );

        return { price: lowestPriceItem.retail, installCost: lowestPriceItem.install };
      };

      // Transform models into model cards
      const cards: AwningModelCard[] = models.map(model => {
        const { price, installCost } = getLowestPriceAndInstall(model.model_id);
        
        return {
          id: model.model_id,
          name: model.name,
          price: price,
          installCost: installCost,
          description: model.description,
          tagline: model.tagline,
          badge: model.badge || undefined,
          bullets: model.bullets,
          bestFor: model.best_for,
          includes: model.includes,
          warrantyYears: model.warranty_years,
          awningType: model.awning_type,
          smartHomeCompatible: model.smart_home_compatible,
          windSensor: model.wind_sensor,
        };
      });

      setModelCards(cards);
    }
  }, [models, pricingByModel, isLoading]);

  useEffect(() => {
    if (!isLoading && addons.length > 0) {
      // Transform addons into addon cards
      const cards: AwningAddOnCard[] = addons
        .filter(addon => addon.addon_id !== 'side-screens')
        .map(addon => ({
          id: addon.addon_id,
          name: addon.name,
          price: addon.price,
          hasQuantity: addon.has_quantity,
          compatibleModels: addon.model_compatibility || undefined,
          description: addon.description,
        }));

      setAddonCards(cards);
    }
  }, [addons, isLoading]);

  // Helper function to get starting price across all models
  const getStartingPrice = (): string => {
    if (isLoading || modelCards.length === 0) {
      return '$2,200'; // Fallback price
    }

    const prices = modelCards
      .filter(card => card.price > 0)
      .map(card => card.price);

    if (prices.length === 0) {
      return '$2,200'; // Fallback if no valid prices
    }

    const lowestPrice = Math.min(...prices);
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0
    }).format(lowestPrice);
  };

  // Helper function to get addons compatible with a specific model
  const getCompatibleAddons = (modelId: string): AwningAddOnCard[] => {
    return addonCards.filter(addon => 
      !addon.compatibleModels || addon.compatibleModels.includes(modelId)
    );
  };

  // Helper function to get model by ID
  const getModelById = (modelId: string): AwningModelCard | undefined => {
    return modelCards.find(card => card.id === modelId);
  };

  return {
    modelCards,
    addonCards,
    isLoading,
    error,
    getStartingPrice,
    getCompatibleAddons,
    getModelById,
  };
};
